"use client";

import * as React from "react";
import { useChromeStrings } from "./chrome-strings";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import Image from "next/image";
import {
  X,
  ThumbsUp,
  MessageSquare,
  Flag,
  Pencil,
  Trash2,
  Check,
  ChevronDown,
  Send,
} from "lucide-react";
import {
  initialComments,
  type Comment,
  type CommentReply,
} from "@/fixtures/shared/article/comment-fixtures";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ConfirmDialog } from "@/components/account/shared/confirm-dialog";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import {
  useCommentThread,
  canModifyComment,
  canSubmitReport,
  COMMENT_SORTS,
  REPORT_OTHER,
  REPORT_REASON_KEYS,
  type CommentSort,
} from "@/hooks/use-comment-thread";

export function ArticleCommentDrawer({
  trigger,
  count = 23,
  seed,
  openOnMount = false,
}: {
  trigger: React.ReactNode;
  count?: number;
  /** The thread to seed with. EJ Insight passes its English fixtures; the
   *  Chinese products take the default. */
  seed?: Comment[];
  /** Open immediately — used when the reader arrived from a card's comment
   *  icon, which links here with `?comments=open`. */
  openOnMount?: boolean;
}) {
  const t = useChromeStrings("comments");
  const [open, setOpen] = React.useState(openOnMount);
  const {
    comments,
    sort,
    setSort,
    mode,
    setMode,
    composer,
    setComposer,
    getDraft,
    setDraft,
    closeOverlay,
    toggleLike,
    addComment,
    addReply,
    editComment,
    deleteComment,
    submitReport,
    findById,
  } = useCommentThread(seed);
  /** Comment queued for deletion; the confirm dialog is open while it is set. */
  const [pendingDelete, setPendingDelete] = React.useState<string | null>(null);

  const total = comments.reduce(
    (acc, c) => acc + 1 + (c.replies?.length ?? 0),
    Math.max(0, count - initialComments.length),
  );

  const [hydrated, setHydrated] = React.useState(false);
  React.useEffect(() => setHydrated(true), []);
  const handleLike = toggleLike;
  const handleSubmit = () => addComment(composer);

  /**
   * What the bottom composer is currently pointed at: a reply target, an edit
   * target, or nothing (a new comment).
   */
  const target =
    mode.kind === "reply" || mode.kind === "edit"
      ? findById(mode.commentId)
      : undefined;
  const targetKind = mode.kind === "reply" || mode.kind === "edit" ? mode.kind : null;

  // Non-modal drawer: no backdrop, page behind stays scrollable & clickable.
  // Built directly on Radix Dialog primitives (modal={false}) instead of the
  // shadcn <Sheet> wrapper, which always renders a backdrop overlay.
  // The trigger is plain until hydration so Radix's useId is not in the
  // server HTML (it does not match the id the browser then computes).
  if (!hydrated) return <>{trigger}</>;
  return (
    <DialogPrimitive.Root modal={false} open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          // Block pointer-event escape into the article behind only inside
          // the drawer; outside the drawer is fully interactive.
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          aria-describedby={undefined}
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right fixed inset-y-0 right-0 z-[80] flex flex-col bg-white shadow-xl ease-in-out data-[state=closed]:duration-200 data-[state=open]:duration-300"
          style={{
            width: 420,
            maxWidth: "100vw",
            fontFamily: CORE_FONT.sans,
            borderLeft: `1px solid ${CORE_COLORS.dividerSoft}`,
          }}
        >
          <DialogPrimitive.Title className="sr-only">
            {t("title")}
          </DialogPrimitive.Title>
        {/* Header */}
        <div
          className="flex flex-col gap-[12px] px-[20px] pt-[20px] pb-[16px]"
          style={{ borderBottom: `1px solid ${CORE_COLORS.dividerBold}` }}
        >
          <div className="flex items-center justify-between">
            <h2
              style={{
                fontFamily: CORE_FONT.sans,
                fontSize: "20px",
                fontWeight: 700,
                color: CORE_COLORS.textPrimary,
                lineHeight: "28px",
              }}
            >
              {t("titleCount", { count: total })}
            </h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("close")}
              className="inline-flex items-center justify-center size-[36px] rounded-full transition-colors duration-150 hover:bg-[#f5f5f7] active:scale-[0.95] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[color:var(--focus-ring)]"
              style={{ ["--focus-ring" as string]: CORE_COLORS.brandGreen } as React.CSSProperties}
            >
              <X className="size-[18px]" color={CORE_COLORS.textPrimary} strokeWidth={2} />
            </button>
          </div>
          <div className="flex items-center gap-[10px]">
            <span
              style={{
                fontFamily: CORE_FONT.condensed,
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: CORE_COLORS.textTertiary,
              }}
            >
              {t("sort")}
            </span>
            <SortMenu value={sort} onChange={setSort} />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Nothing composes inline. Replying AND editing both drive the one
              composer at the bottom of the thread — "all replies should be
              made using the main 'create comment' field" and the same for
              edits — so the drawer never grows a second text box. */}
          {comments.map((c) => (
            <ArticleCommentItem
              key={c.id}
              comment={c}
              onLike={handleLike}
              onReplyClick={(id) => setMode({ kind: "reply", commentId: id })}
              onEditClick={(id) => setMode({ kind: "edit", commentId: id })}
              onDeleteClick={setPendingDelete}
              onReport={submitReport}
            />
          ))}
        </div>

        {/* Composer */}
        <div
          className="p-[16px]"
          style={{ borderTop: `1px solid ${CORE_COLORS.dividerBold}` }}
        >
          {/* One field for new comments, replies and edits. With a target it
              grows a label above the box naming what the draft is for, plus an
              X to drop back to a plain comment; drafts are keyed per target,
              so switching away and back restores what was typed. */}
          {target && targetKind ? (
            <CommentComposer
              autoFocus
              label={
                targetKind === "reply" ? t("replyingTo", { author: target.author }) : t("edit")
              }
              onDismiss={closeOverlay}
              value={getDraft(
                targetKind,
                target.id,
                targetKind === "edit" ? target.body : "",
              )}
              onChange={(t) => setDraft(targetKind, target.id, t)}
              onSubmit={(t) =>
                targetKind === "reply"
                  ? addReply(target.id, t)
                  : editComment(target.id, t)
              }
              placeholder={
                targetKind === "reply" ? t("replyPlaceholder") : t("editPlaceholder")
              }
              ariaLabel={targetKind === "reply" ? t("replyLabel") : t("commentLabel")}
              sendLabel={targetKind === "reply" ? t("replySend") : t("editSave")}
              sendIcon={targetKind === "reply" ? "send" : "check"}
            />
          ) : (
            <CommentComposer
              value={composer}
              onChange={setComposer}
              onSubmit={handleSubmit}
              placeholder={t("placeholder")}
              ariaLabel={t("commentLabel")}
              sendLabel={t("send")}
            />
          )}
        </div>

        {/* Deleting is irreversible, so it asks first — QA: "for my comments,
            allow delete comment, confirmation before deleting". Raised above
            the drawer's own z-index, since the drawer sits at z-80. */}
        <ConfirmDialog
          open={pendingDelete !== null}
          onOpenChange={(next) => {
            if (!next) setPendingDelete(null);
          }}
          zIndex={90}
          title={t("deleteTitle")}
          description={t("deleteBody")}
          confirmLabel={t("delete")}
          // `ConfirmDialog` defaults its cancel to 「取消」, which is right for
          // the account flows it was written for and wrong in the English
          // edition — QA: "missing localisation for the cancel button in delete
          // comment modal".
          cancelLabel={t("cancel")}
          onConfirm={() => {
            if (pendingDelete) deleteComment(pendingDelete);
            setPendingDelete(null);
          }}
        />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

type RowHandlers = {
  onLike: (id: string) => void;
  onReplyClick: (id: string) => void;
  onEditClick: (id: string) => void;
  onDeleteClick: (id: string) => void;
  /** Reporting resolves inside a popover on the row, so the row submits it. */
  onReport: (reason: string | null, otherText: string) => boolean;
};

function ArticleCommentItem({
  comment,
  ...handlers
}: { comment: Comment } & RowHandlers) {
  const t = useChromeStrings("comments");
  return (
    <div
      className="px-[20px] py-[16px]"
      style={{ borderBottom: `1px solid ${CORE_COLORS.dividerSoft}` }}
    >
      <CommentRow comment={comment} {...handlers} />
      {comment.replies && comment.replies.length > 0 && (
        <div
          className="mt-[12px] pl-[16px] flex flex-col gap-[14px]"
          style={{ borderLeft: `2px solid ${CORE_COLORS.brandGreen}` }}
        >
          {comment.replies.map((r) => (
            <CommentRow key={r.id} comment={r} {...handlers} isReply />
          ))}
        </div>
      )}
    </div>
  );
}

function CommentRow({
  comment,
  onLike,
  onReplyClick,
  onEditClick,
  onDeleteClick,
  onReport,
  isReply,
}: {
  comment: Comment | CommentReply;
  isReply?: boolean;
} & RowHandlers) {
  const t = useChromeStrings("comments");
  const isDeleted = (comment as Comment).isDeleted;
  const isOwn = (comment as Comment).isOwn;
  /**
   * Own comments are editable and deletable for six hours after posting; past
   * that the controls disappear rather than being offered and refused — QA:
   * "the edit and delete button will disable/disappear after 6 hours of the
   * time the comment was made".
   */
  const modifiable = canModifyComment(comment);

  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex items-center gap-[8px]">
        <div className="relative size-[28px] rounded-full overflow-hidden shrink-0 bg-[#f5f5f7]">
          <Image
            src={comment.avatar}
            alt={comment.author}
            fill
            className="object-cover"
            sizes="28px"
          />
        </div>
        <span
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: "14px",
            fontWeight: 700,
            color: CORE_COLORS.textPrimary,
            lineHeight: "19px",
          }}
        >
          {comment.author}
        </span>
        <span
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: "12px",
            color: CORE_COLORS.textTertiary,
            lineHeight: "14px",
          }}
        >
          · {comment.time}
          {/* Additive — the original post time above is never overwritten.
              QA: "編輯訊息後，時間戳不應該被編輯日期時間覆蓋掉吧？ 而且如果有
              編輯過，最好在後面顯示編輯過的時間". */}
          {comment.editedAt ? t("edited", { time: comment.editedAt }) : ""}
        </span>
      </div>

      {isDeleted ? (
        <p
          className="italic text-center py-[8px]"
          style={{
            fontSize: "13px",
            color: CORE_COLORS.textTertiary,
            lineHeight: "18px",
          }}
        >
          {t("deleted")}
        </p>
      ) : (
        <p
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: "14px",
            color: CORE_COLORS.textPrimary,
            lineHeight: "20px",
          }}
        >
          {comment.body}
        </p>
      )}

      <div className="flex items-center gap-[4px]">
        <button
          type="button"
          onClick={() => onLike(comment.id)}
          // Coerced: `liked` is optional, and an undefined value drops the
          // attribute entirely, leaving a toggle button with no state.
          aria-pressed={!!comment.liked}
          aria-label={t("likeAria", { count: comment.likes })}
          className="inline-flex items-center gap-[4px] px-[8px] py-[6px] rounded-full transition-colors duration-150 hover:bg-[#f1f9f5] active:scale-[0.97] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--green)]"
          style={{ ["--green" as string]: CORE_COLORS.brandGreen } as React.CSSProperties}
        >
          <ThumbsUp
            className="size-[15px]"
            color={comment.liked ? CORE_COLORS.brandGreen : CORE_COLORS.textSecondary}
            fill={comment.liked ? CORE_COLORS.brandGreen : "none"}
            strokeWidth={1.8}
          />
          <span
            className="tabular-nums"
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: "12px",
              fontWeight: comment.liked ? 700 : 500,
              color: comment.liked
                ? CORE_COLORS.brandGreen
                : CORE_COLORS.textSecondary,
              lineHeight: "16px",
            }}
          >
            {comment.likes}
          </span>
        </button>
        {!isReply && (
          <button
            type="button"
            onClick={() => onReplyClick(comment.id)}
            aria-label={t("reply")}
            className="inline-flex items-center gap-[4px] px-[8px] py-[6px] rounded-full transition-colors duration-150 hover:bg-[#f5f5f7] active:scale-[0.97] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--green)]"
            style={{ ["--green" as string]: CORE_COLORS.brandGreen } as React.CSSProperties}
          >
            <MessageSquare
              className="size-[15px]"
              color={CORE_COLORS.textSecondary}
              strokeWidth={1.8}
            />
            <span
              style={{
                fontSize: "12px",
                fontWeight: 500,
                color: CORE_COLORS.textSecondary,
                lineHeight: "16px",
              }}
            >
              {t("replyShort")}
            </span>
          </button>
        )}
        {isOwn ? (
          modifiable && (
            <div className="ml-auto flex items-center gap-[4px]">
              <button
                type="button"
                onClick={() => onEditClick(comment.id)}
                aria-label={t("edit")}
                className="inline-flex items-center gap-[4px] px-[8px] py-[6px] rounded-full transition-colors duration-150 hover:bg-[#f5f5f7] active:scale-[0.97] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--green)]"
                style={{ ["--green" as string]: CORE_COLORS.brandGreen } as React.CSSProperties}
              >
                <Pencil
                  className="size-[15px]"
                  color={CORE_COLORS.textSecondary}
                  strokeWidth={1.8}
                />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: CORE_COLORS.textSecondary,
                    lineHeight: "16px",
                  }}
                >
                  {t("editShort")}
                </span>
              </button>
              <button
                type="button"
                onClick={() => onDeleteClick(comment.id)}
                aria-label={t("deleteAria")}
                className="inline-flex items-center gap-[4px] px-[8px] py-[6px] rounded-full transition-colors duration-150 hover:bg-[#fdecee] active:scale-[0.97] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--red)]"
                style={{ ["--red" as string]: CORE_COLORS.brandRed } as React.CSSProperties}
              >
                <Trash2
                  className="size-[15px]"
                  color={CORE_COLORS.brandRed}
                  strokeWidth={1.8}
                />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: CORE_COLORS.brandRed,
                    lineHeight: "16px",
                  }}
                >
                  {t("deleteShort")}
                </span>
              </button>
            </div>
          )
        ) : (
          !isDeleted && <ReportButton onSubmit={onReport} />
        )}
      </div>
    </div>
  );
}

/**
 * The 排序 menu.
 *
 * The trigger used to be a `<button>` labelled 最新 with a chevron and no menu
 * behind it. Options come from the thread hook so every drawer skin offers the
 * same three. `zIndex` has to clear the drawer's own 80.
 */
function SortMenu({
  value,
  onChange,
}: {
  value: CommentSort;
  onChange: (next: CommentSort) => void;
}) {
  const t = useChromeStrings("comments");
  const [open, setOpen] = React.useState(false);
  const current = COMMENT_SORTS.find((s) => s.value === value) ?? COMMENT_SORTS[0];
  /** The hook's labels are Chinese — the values are the identifiers, so the
   *  words are looked up here. */
  const sortLabel = (v: CommentSort) =>
    t(
      v === "newest"
        ? "sortNewest"
        : v === "oldest"
          ? "sortOldest"
          : v === "replies"
            ? "sortReplies"
            : "sortLikes",
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("sortAria", { label: sortLabel(current.value) })}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="inline-flex items-center gap-[4px] px-[6px] py-[4px] rounded-[2px] transition-colors duration-150 hover:bg-[#f5f5f7] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[color:var(--green)]"
          style={
            {
              fontFamily: CORE_FONT.sans,
              fontSize: "13px",
              fontWeight: 700,
              color: CORE_COLORS.textPrimary,
              ["--green" as string]: CORE_COLORS.brandGreen,
            } as React.CSSProperties
          }
        >
          {sortLabel(current.value)}
          <ChevronDown
            className="size-[14px] transition-transform duration-150 motion-reduce:transition-none"
            strokeWidth={2}
            style={{ transform: open ? "rotate(180deg)" : undefined }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        role="listbox"
        className="bg-white p-[4px]"
        style={{
          zIndex: 90,
          width: 148,
          borderRadius: 2,
          border: `1px solid rgba(0,0,0,0.15)`,
          fontFamily: CORE_FONT.sans,
        }}
      >
        {COMMENT_SORTS.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between gap-[8px] px-[10px] py-[8px] text-left transition-colors duration-150 hover:bg-[#f5f5f7] motion-reduce:transition-none focus-visible:outline-none focus-visible:bg-[#f5f5f7]"
              style={{
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                color: active ? CORE_COLORS.brandGreen : CORE_COLORS.textPrimary,
                borderRadius: 2,
              }}
            >
              {sortLabel(option.value)}
              {active && (
                <Check
                  className="size-[14px]"
                  color={CORE_COLORS.brandGreen}
                  strokeWidth={2.4}
                />
              )}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}


/**
 * 檢舉評論 — a popover hanging off the 檢舉 button on the row.
 *
 * It was a full-drawer takeover that hid the thread behind it. QA: "do not
 * open a new page on the modal for it, use popover instead". Reporting is one
 * short choice, so it resolves in place next to the comment it is about; the
 * reader can still see which comment they are reporting.
 *
 * The reason list and the "其他 requires text" rule come from
 * `useCommentThread`, so all five drawer skins validate identically.
 */
function ReportButton({
  onSubmit,
}: {
  onSubmit: (reason: string | null, otherText: string) => boolean;
}) {
  const t = useChromeStrings("comments");
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState<string | null>(null);
  const [otherText, setOtherText] = React.useState("");
  const submittable = canSubmitReport(reason, otherText);

  const reset = () => {
    setReason(null);
    setOtherText("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("report")}
          className="inline-flex items-center gap-[4px] px-[8px] py-[6px] ml-auto rounded-full transition-colors duration-150 hover:bg-[#fdecee] active:scale-[0.97] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--red)]"
          style={{ ["--red" as string]: CORE_COLORS.brandRed } as React.CSSProperties}
        >
          <Flag
            className="size-[15px]"
            color={CORE_COLORS.brandRed}
            strokeWidth={1.8}
          />
          <span
            style={{
              fontSize: "12px",
              fontWeight: 500,
              color: CORE_COLORS.brandRed,
              lineHeight: "16px",
            }}
          >
            {t("reportShort")}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        collisionPadding={12}
        className="bg-white p-[14px]"
        style={{
          // Above the drawer's own z-80, or it would open behind it.
          zIndex: 90,
          width: 268,
          borderRadius: 3,
          border: `1px solid rgba(0,0,0,0.15)`,
          fontFamily: CORE_FONT.sans,
        }}
      >
        <p
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: CORE_COLORS.textPrimary,
            lineHeight: "18px",
            marginBottom: 8,
          }}
        >
          {t("reportTitle")}
        </p>
        <div role="radiogroup" aria-label={t("reportReason")} className="flex flex-col">
          {REPORT_REASON_KEYS.map(({ value: option, key: reasonKey }) => {
            const active = reason === option;
            return (
              <label
                key={option}
                className="flex cursor-pointer items-center gap-[8px] px-[6px] py-[7px] transition-colors duration-150 hover:bg-[#f5f5f7] motion-reduce:transition-none"
                style={{
                  borderRadius: 2,
                  fontSize: 13,
                  lineHeight: "18px",
                  fontWeight: active ? 700 : 500,
                  color: active ? CORE_COLORS.textPrimary : CORE_COLORS.textSecondary,
                }}
              >
                <input
                  type="radio"
                  name="report-reason"
                  value={option}
                  checked={active}
                  onChange={() => setReason(option)}
                  className="size-[14px] shrink-0 accent-[color:var(--green)]"
                  style={{ ["--green" as string]: CORE_COLORS.brandGreen } as React.CSSProperties}
                />
                {t(reasonKey)}
              </label>
            );
          })}
        </div>

        {/* Only 其他 asks for text, and then it is required — QA: "當選擇「其他」
            時，應該顯示一個新的輸入原因欄位…並且在允許使用者提交之前，驗證該
            原因欄位不得為空白". */}
        {reason === REPORT_OTHER && (
          <textarea
            autoFocus
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder={t("reportOtherPlaceholder")}
            rows={3}
            aria-label={t("reportOtherLabel")}
            className="mt-[8px] w-full resize-none outline-none p-[8px] transition-colors duration-150 focus:border-[color:var(--green)] motion-reduce:transition-none"
            style={
              {
                border: `1px solid rgba(0,0,0,0.15)`,
                borderRadius: 2,
                fontSize: 13,
                lineHeight: "18px",
                color: CORE_COLORS.textPrimary,
                ["--green" as string]: CORE_COLORS.brandGreen,
              } as React.CSSProperties
            }
          />
        )}

        <div className="mt-[12px] flex items-center justify-end gap-[8px]">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="transition-colors duration-150 hover:bg-[#f5f5f7] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[color:var(--green)]"
            style={
              {
                fontFamily: CORE_FONT.condensed,
                fontSize: 13,
                fontWeight: 700,
                color: CORE_COLORS.textSecondary,
                padding: "7px 12px",
                border: `1px solid rgba(0,0,0,0.2)`,
                borderRadius: 2,
                ["--green" as string]: CORE_COLORS.brandGreen,
              } as React.CSSProperties
            }
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              if (onSubmit(reason, otherText)) setOpen(false);
            }}
            disabled={!submittable}
            className="transition-[background-color,transform] duration-150 enabled:hover:opacity-90 active:scale-[0.97] disabled:cursor-not-allowed disabled:active:scale-100 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[color:var(--green)]"
            style={
              {
                fontFamily: CORE_FONT.condensed,
                fontSize: 13,
                fontWeight: 700,
                color: "white",
                padding: "7px 12px",
                // Grey, not a dimmed brand green — QA: "如果冇得滑動嘅話應該
                // 係要灰色 而唔係綠色".
                backgroundColor: submittable
                  ? CORE_COLORS.brandGreen
                  : CORE_COLORS.disabledSurface,
                borderRadius: 2,
                ["--green" as string]: CORE_COLORS.brandGreen,
              } as React.CSSProperties
            }
          >
            {t("reportSubmit")}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The one comment input.
 *
 * Reply and edit used to render their own textarea — a differently sized,
 * differently bordered field with a separate 取消/發送 button pair below it, so
 * replying looked nothing like commenting. QA: "the comment input should use
 * the same textarea as the make new comment area". This is that field, with an
 * optional label row inside the same border naming what the draft is for and
 * an X to abandon it.
 */
function CommentComposer({
  value,
  onChange,
  onSubmit,
  placeholder,
  ariaLabel,
  sendLabel,
  sendIcon = "send",
  label,
  onDismiss,
  autoFocus,
}: {
  value: string;
  onChange: (text: string) => void;
  onSubmit: (text: string) => void;
  placeholder: string;
  ariaLabel: string;
  sendLabel: string;
  sendIcon?: "send" | "check";
  /** Names the target of a reply or edit; omitted for a new comment. */
  label?: string;
  onDismiss?: () => void;
  autoFocus?: boolean;
}) {
  const t = useChromeStrings("comments");
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  /**
   * Focus on the label changing, not just on mount.
   *
   * The bottom composer switches between "new comment" and t("replyingTo", { author: "" }) at the
   * same position in the tree, so React updates the existing textarea rather
   * than remounting it — the `autoFocus` attribute would never fire again
   * after the first render, and clicking 回覆 would leave the caret nowhere.
   */
  React.useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus, label]);

  return (
    <div className="flex flex-col gap-[6px]">
      {/* The label sits ABOVE the field, outside its border, in ordinary text
          colour, with the X immediately beside the name rather than pushed to
          the far edge — it reads as a caption on the composer, not as a chip
          inside the input. */}
      {label && (
        <div className="flex items-center gap-[2px] pl-[2px]">
          <span
            className="min-w-0 truncate"
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: "12px",
              fontWeight: 700,
              color: CORE_COLORS.textSecondary,
              lineHeight: "16px",
            }}
          >
            {label}
          </span>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label={t("discard")}
              className="inline-flex size-[20px] shrink-0 items-center justify-center rounded-full transition-colors duration-150 hover:bg-[#ececee] active:scale-[0.95] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--green)]"
              style={{ ["--green" as string]: CORE_COLORS.brandGreen } as React.CSSProperties}
            >
              <X
                className="size-[13px]"
                color={CORE_COLORS.textSecondary}
                strokeWidth={2}
              />
            </button>
          )}
        </div>
      )}
      <div
        className="flex items-end gap-[8px] p-[8px] transition-colors duration-150 focus-within:border-[color:var(--green)] motion-reduce:transition-none"
        style={
          {
            border: `1px solid rgba(0,0,0,0.15)`,
            borderRadius: "2px",
            backgroundColor: "white",
            ["--green" as string]: CORE_COLORS.brandGreen,
          } as React.CSSProperties
        }
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          aria-label={ariaLabel}
          className="flex-1 resize-none bg-transparent outline-none"
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: "14px",
            lineHeight: "20px",
            color: CORE_COLORS.textPrimary,
          }}
        />
        <button
          type="button"
          aria-label={sendLabel}
          onClick={() => onSubmit(value)}
          disabled={!value.trim()}
          className="inline-flex items-center justify-center size-[44px] shrink-0 transition-[background-color,transform] duration-150 enabled:hover:opacity-90 active:scale-[0.94] disabled:cursor-not-allowed disabled:active:scale-100 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--green)]"
          style={
            {
              // Empty composer reads as grey, per the same rule as the report
              // submit above.
              backgroundColor: value.trim()
                ? CORE_COLORS.brandGreen
                : CORE_COLORS.disabledSurface,
              borderRadius: "2px",
              ["--green" as string]: CORE_COLORS.brandGreen,
            } as React.CSSProperties
          }
        >
          {sendIcon === "check" ? (
            <Check className="size-[18px]" color="white" strokeWidth={2.4} />
          ) : (
            <Send className="size-[18px]" color="white" strokeWidth={2} />
          )}
        </button>
      </div>
    </div>
  );
}
